import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  Cpu,
  Atom,
  Scale,
  Activity,
  Link2,
  User,
  Sparkles,
  Zap,
  Binary,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const iconMap = {
  brain: Brain,
  cpu: Cpu,
  atom: Atom,
  scale: Scale,
  sparkles: Sparkles,
  activity: Activity,
  link: Link2,
  user: User,
};

// ── Interdependent research mesh ──────────────────────────────────────────
const researchDomains = [
  {
    name: "Consciousness",
    icon: Brain,
    color: "blue",
    role: "Central research domain examining awareness, experience, causation, and the foundations of reality.",
  },
  {
    name: "Fundamental Physics",
    icon: Atom,
    color: "purple",
    role: "Explores how consciousness may relate to causation, relativity, quantum mechanics, and the structure of reality.",
  },
  {
    name: "Artificial Intelligence",
    icon: Cpu,
    color: "emerald",
    role: "Investigates implications of consciousness research for AI, human intelligence, legal accountability, and human-in-the-loop approaches.",
  },
  {
    name: "Neurotechnology",
    icon: Zap,
    color: "amber",
    role: "Examines how brain-based technologies and interfaces may help reveal hidden dimensions of cognition and conscious experience.",
  },
  {
    name: "Quantum Computing",
    icon: Binary,
    color: "rose",
    role: "Explores conceptual and scientific links between quantum behaviour, information, and consciousness.",
  },
];

// ── Mesh diagram component ────────────────────────────────────────────────
const MeshDiagram = () => {
  const [hoveredNode, setHoveredNode] = useState(null);

  const nodePositions = [
    { x: 200, y: 60, domain: researchDomains[0] },
    { x: 60, y: 160, domain: researchDomains[1] },
    { x: 340, y: 160, domain: researchDomains[2] },
    { x: 100, y: 300, domain: researchDomains[3] },
    { x: 300, y: 300, domain: researchDomains[4] },
  ];

  const colorMap = {
    blue: { fill: "#3B82F6", stroke: "#1D4ED8", bg: "#DBEAFE" },
    purple: { fill: "#8B5CF6", stroke: "#6D28D9", bg: "#EDE9FE" },
    emerald: { fill: "#10B981", stroke: "#047857", bg: "#D1FAE5" },
    amber: { fill: "#F59E0B", stroke: "#B45309", bg: "#FEF3C7" },
    rose: { fill: "#F43F5E", stroke: "#BE123C", bg: "#FFE4E6" },
  };

  const connections = [];
  for (let i = 0; i < nodePositions.length; i++) {
    for (let j = i + 1; j < nodePositions.length; j++) {
      connections.push({ from: nodePositions[i], to: nodePositions[j] });
    }
  }

  return (
    <div className="relative w-full max-w-lg mx-auto">
      <svg viewBox="0 0 400 380" className="w-full h-auto">
        {connections.map((conn, idx) => (
          <line
            key={idx}
            x1={conn.from.x}
            y1={conn.from.y}
            x2={conn.to.x}
            y2={conn.to.y}
            stroke="#CBD5E1"
            strokeWidth="2"
            strokeDasharray="4 2"
            className="transition-all duration-300"
            style={{
              opacity: hoveredNode
                ? hoveredNode === conn.from.domain.name ||
                  hoveredNode === conn.to.domain.name
                  ? 1
                  : 0.2
                : 0.6,
            }}
          />
        ))}
        {nodePositions.map((node, idx) => {
          const colors = colorMap[node.domain.color];
          const isHovered = hoveredNode === node.domain.name;
          const Icon = node.domain.icon;
          return (
            <g
              key={idx}
              onMouseEnter={() => setHoveredNode(node.domain.name)}
              onMouseLeave={() => setHoveredNode(null)}
              className="cursor-pointer transition-transform duration-300"
              style={{
                transform: isHovered ? "scale(1.1)" : "scale(1)",
                transformOrigin: `${node.x}px ${node.y}px`,
              }}
            >
              <circle
                cx={node.x}
                cy={node.y}
                r={isHovered ? 42 : 38}
                fill={colors.bg}
                stroke={colors.stroke}
                strokeWidth="3"
                className="transition-all duration-300"
              />
              <circle
                cx={node.x}
                cy={node.y}
                r={24}
                fill={colors.fill}
                className="transition-all duration-300"
              />
              <foreignObject x={node.x - 12} y={node.y - 12} width="24" height="24">
                <Icon className="w-6 h-6 text-white" />
              </foreignObject>
              <text
                x={node.x}
                y={node.y + 55}
                textAnchor="middle"
                className="text-xs font-medium fill-slate-700"
              >
                {node.domain.name}
              </text>
            </g>
          );
        })}
      </svg>

      {hoveredNode && (
        <div className="absolute bottom-0 left-0 right-0 text-center p-3 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200">
          <p className="text-sm text-slate-600">
            {researchDomains.find((d) => d.name === hoveredNode)?.role}
          </p>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// Research Page (framework + themes only — outputs live on /publications)
// ══════════════════════════════════════════════════════════════════════════
export const ResearchPage = () => {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/research-themes`)
      .then((res) => setThemes(res.data))
      .catch((err) => console.error("Error fetching research themes:", err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div id="main-content" className="pt-20" data-testid="research-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="overline text-brand-primary mb-4">Research</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900 leading-tight mb-6">
              Research at the Centre for Consciousness Studies
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-5">
              The Centre pursues rigorous interdisciplinary research across consciousness, fundamental physics, artificial intelligence, neurotechnology, and quantum computing.
            </p>
            <p className="text-base text-slate-600 leading-relaxed">
              We believe these fields should not develop as separate silos, but as deeply interconnected domains that can enrich and inform one another. By strengthening research within each area and building coherence across them, the Centre generates new knowledge, unlocks transformative insights, and shapes a more integrated scientific understanding of consciousness, intelligence, and reality.
            </p>
          </div>
        </div>
      </section>

      {/* Interdependent Research Mesh */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="mesh-diagram-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="overline text-brand-primary mb-4">
              Interdependent Research Mesh
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-4">
              Five Interconnected Domains
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              These domains are explored as interconnected fields that strengthen one another and together contribute to a more coherent understanding of consciousness, intelligence, and reality.
            </p>
          </div>

          <MeshDiagram />

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-12">
            {researchDomains.map((domain, idx) => {
              const Icon = domain.icon;
              const colorClasses = {
                blue: "bg-blue-50 border-blue-200 text-blue-700",
                purple: "bg-purple-50 border-purple-200 text-purple-700",
                emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
                amber: "bg-amber-50 border-amber-200 text-amber-700",
                rose: "bg-rose-50 border-rose-200 text-rose-700",
              };
              return (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border ${colorClasses[domain.color]}`}
                >
                  <Icon className="w-6 h-6 mb-2" />
                  <h3 className="font-semibold text-sm mb-1">{domain.name}</h3>
                  <p className="text-xs opacity-80">{domain.role}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Research Themes Grid */}
      <section className="py-16 lg:py-24 bg-white" data-testid="themes-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <p className="overline text-brand-primary mb-4">Our Portfolio</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Seven Research Themes
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Each theme explores a major question about consciousness, mind, intelligence, and the human future shaped by science and technology.
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 gap-8">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-8 animate-pulse">
                  <div className="w-12 h-12 bg-slate-200 rounded-lg mb-4"></div>
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-slate-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-slate-200 rounded w-5/6"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {themes.map((theme, index) => {
                const IconComponent = iconMap[theme.icon] || Brain;
                return (
                  <Card
                    key={theme.id}
                    className="bg-white border border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                    data-testid={`research-theme-${index}`}
                  >
                    <CardContent className="p-8">
                      <div className="flex items-start gap-6">
                        <div className="w-14 h-14 rounded-lg bg-brand-highlight flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-7 h-7 text-brand-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-serif text-2xl text-slate-900 mb-3">
                            {theme.title}
                          </h3>
                          <p className="text-slate-600 leading-relaxed mb-4">
                            {theme.description}
                          </p>
                          <div className="bg-slate-50 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">
                              Why This Matters
                            </h4>
                            <p className="text-sm text-slate-600">
                              {theme.why_it_matters}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Interdisciplinary Approach */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="overline text-brand-primary mb-4">Our Approach</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Interdisciplinary Integration
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Questions of consciousness, mind, and intelligence cannot be adequately addressed within any single discipline. The Centre fosters collaboration across philosophy, neuroscience, physics, computer science, cognitive science, and ethics to develop integrated frameworks that advance understanding.
            </p>
            <div className="grid sm:grid-cols-3 gap-6 text-left">
              <div className="p-6 bg-white rounded-lg border border-slate-200">
                <h3 className="font-serif text-lg text-slate-900 mb-2">
                  Theoretical Foundations
                </h3>
                <p className="text-sm text-slate-600">
                  Developing rigorous conceptual and theoretical frameworks for understanding consciousness and cognition.
                </p>
              </div>
              <div className="p-6 bg-white rounded-lg border border-slate-200">
                <h3 className="font-serif text-lg text-slate-900 mb-2">
                  Empirical Research
                </h3>
                <p className="text-sm text-slate-600">
                  Engaging with experimental neuroscience and empirical psychology to ground theoretical work.
                </p>
              </div>
              <div className="p-6 bg-white rounded-lg border border-slate-200">
                <h3 className="font-serif text-lg text-slate-900 mb-2">Applied Ethics</h3>
                <p className="text-sm text-slate-600">
                  Translating fundamental research into practical guidance for technology governance and policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Outputs — pointer to /publications */}
      <section
        className="py-16 lg:py-24 bg-slate-900 text-white"
        data-testid="outputs-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="overline text-blue-300 mb-4">Research Outputs</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-white mb-6">
                From Theory to Impact
              </h2>
              <p className="text-slate-300 leading-relaxed mb-6">
                Our research contributes to scholarly discourse through peer-reviewed publications, conference presentations, video interviews, and public commentary. Browse the full record on the Publications page.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-white text-slate-900 hover:bg-slate-100"
                  data-testid="view-publications-btn"
                >
                  <Link to="/publications">
                    View Publications &amp; Media <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-slate-600 text-white hover:bg-slate-800 bg-transparent"
                  data-testid="watch-videos-btn"
                >
                  <Link to="/publications#videos">Watch Videos</Link>
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800 rounded-lg p-6 text-center">
                <div className="text-4xl font-serif text-white mb-2">16</div>
                <div className="text-sm text-slate-400">Publications</div>
              </div>
              <div className="bg-slate-800 rounded-lg p-6 text-center">
                <div className="text-4xl font-serif text-white mb-2">7</div>
                <div className="text-sm text-slate-400">Research Themes</div>
              </div>
              <div className="bg-slate-800 rounded-lg p-6 text-center">
                <div className="text-4xl font-serif text-white mb-2">3</div>
                <div className="text-sm text-slate-400">Physics Presentations</div>
              </div>
              <div className="bg-slate-800 rounded-lg p-6 text-center">
                <div className="text-4xl font-serif text-white mb-2">7</div>
                <div className="text-sm text-slate-400">Video Resources</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Directions */}
      <section className="py-16 lg:py-24 bg-white" data-testid="future-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <p className="overline text-brand-primary mb-4">Looking Ahead</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Future Research Directions
            </h2>
            <div className="prose-custom">
              <p>
                As AI and neurotechnology continue to evolve, new and important questions are coming into focus. The Centre is committed to exploring these emerging areas through rigorous, interdisciplinary research, with particular attention to:
              </p>
              <ul className="space-y-3 my-6">
                <li>How large language models relate to understanding, reasoning, and consciousness</li>
                <li>The implications of advanced brain-computer interfaces for identity and personhood</li>
                <li>Possible links between quantum computing and theories of consciousness</li>
                <li>Ethical approaches to neurorights and cognitive liberty</li>
                <li>The value of indigenous perspectives in broadening how we understand mind and consciousness</li>
              </ul>
              <p>
                We welcome collaboration with researchers and institutions who share our commitment to careful, open, and forward-looking inquiry into these vital questions.
              </p>
            </div>
            <div className="mt-8">
              <Button
                asChild
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="contact-collaborate-btn"
              >
                <Link to="/contact">
                  Discuss Collaboration <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResearchPage;
